import { Dispatch, FC, SetStateAction } from "react";
import { FormType } from "../../common";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { DairyIntervention } from "../../../../models/Intervention";
import { ArrayField, useFormContext } from "react-hook-form";
import { CattleFeed } from "../../../../models/Intervention/CattleIntervention";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import RowControlledTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import SustellRationFeeds from "../../../../../modules/Farms/Baseline/SustellRationFeeds_v2";
import { AnimalType, CompoundFeedDatabase, MasterDataOption, Maybe } from "../../../../../../graphql/types";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import { Spacer } from "../../../helpers/Spacer";
import EditModeButtons from "../../../helpers/EditModeButtons";

interface CompoundFeed {
  display_name: string;
  value: string;
  databaseFoundation: CompoundFeedDatabase;
};

interface DairyV2FeedCompoundFeedComponentProp {
  compoundFeeds?: Maybe<Array<MasterDataOption>>;
  databaseFoundation: CompoundFeedDatabase;
  formType: FormType;
  fieldItemPrefix: string;
  currentStageData: CattleFeed;
  isCompFeedEdit: boolean;
  setIsCompFeedEdit: Dispatch<SetStateAction<boolean>>;
  feedsAdditions: Partial<ArrayField<Record<string, any>, "keyId">>[];
  appendFeedsAdditions: (value: Partial<Record<string, any>> | Partial<Record<string, any>>[], shouldFocus?: boolean) => void;
  removeFeedsAdditions: (index?: number | number[]) => void;
  resetEdit: () => void;
};

const DairyV2FeedCompoundFeedComponent: FC<DairyV2FeedCompoundFeedComponentProp> = ({
  compoundFeeds,
  databaseFoundation,
  formType = FormType.Add,
  fieldItemPrefix,
  currentStageData,
  isCompFeedEdit,
  setIsCompFeedEdit,
  feedsAdditions,
  appendFeedsAdditions,
  removeFeedsAdditions,
  resetEdit,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fc = useFormContext<DairyIntervention>();

  const animalType = (fc.getValues("animalType") as AnimalType) ?? undefined;
  const barnOutputMassUnit = "kg DM";

  const getBaselineCompoundFeedLabel = (id: string) => {
    const feedDisplayName = compoundFeeds?.find(
      (item) => item.value === id
    )?.display_name;
    return feedDisplayName || "unknown";
  };

  const getCompoundFeedsList = (databaseOption: CompoundFeedDatabase) => {
    const feeds = (compoundFeeds as unknown as CompoundFeed[]) || [];
    return feeds.filter(
      (feed: CompoundFeed) => feed.databaseFoundation === databaseOption
    );
  };

  return (
    <DsmGrid className={classes.dsmGridOneColumnWithBorder}>
      <Spacer />
      {(currentStageData?.compoundFeeds || [])
        ?.filter((item) => !!item.feedType)
        ?.map((feedItem, i: number) => (
          !isCompFeedEdit &&
          !isFieldFilled(fc.getValues(`${fieldItemPrefix}.compoundFeeds[${i}].kgPerAnimal`))
        ) ? (
          <DsmGrid className={classes.dsmGridTwoColumnsInterventionComponent}>
            <ReactHookDsmInput
              key={`${fieldItemPrefix}.compoundFeeds[${i}].originalKgPerAnimal`}
              defaultValue={feedItem.kgPerAnimal ?? ''}
              name={`${fieldItemPrefix}.compoundFeeds[${i}].originalKgPerAnimal`}
              label={getBaselineCompoundFeedLabel(feedItem.feedType)}
              adornment={barnOutputMassUnit}
              tooltip={intl.formatMessage({ id: "COMPOUND_FEED.AMOUNT.DAIRY.TOOLTIP" })}
              disabled={true}
              type="number"
            />
          </DsmGrid>
        ) : (
          <>
            <RowControlledTextFieldWithMetrics
              name={`${fieldItemPrefix}.compoundFeeds[${i}].kgPerAnimal`}
              label={getBaselineCompoundFeedLabel(feedItem.feedType)}
              tooltip={intl.formatMessage({
                id: "INTERVENTION.FORM.BARN.RATION.COMPOUND_FEEDS.TOOLTIP",
              })}
              metricUnit={barnOutputMassUnit}
              baseline={feedItem.kgPerAnimal}
              disabled={formType === "view" || !isCompFeedEdit}
              minAllowedValue={0}
              displayFormattedUnit={false}
              originalMetricUnit={barnOutputMassUnit}
              displayTypeSelect={isCompFeedEdit}
            />
            <Spacer />
          </>
        )
        )}
      {(isCompFeedEdit ||
        (!isCompFeedEdit && feedsAdditions?.length !== 0)) && (
          <SustellRationFeeds
            key="compoundFeedsAdditions"
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.FEED.COMPOUND_FEED",
            })}
            availableFeedItems={getCompoundFeedsList(databaseFoundation)}
            fieldItemPrefix={fieldItemPrefix}
            subFieldName="compoundFeedsAdditions"
            tooltip={intl.formatMessage({
              id: "BASELINE.FORM.BARN.RATION.COMPOUND_FEEDS.TOOLTIP",
            })}
            formState={isCompFeedEdit ? formType : FormType.View}
            compoundFeed
            feedData={feedsAdditions}
            fieldsAppenRemoveObject={{
              fields: feedsAdditions,
              append: appendFeedsAdditions,
              remove: removeFeedsAdditions,
            }}
            allowNewFeeds={isCompFeedEdit}
            animalType={animalType}
            style={{ marginTop: "4px" }}
          />
        )}
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isCompFeedEdit}
          onEdit={() => setIsCompFeedEdit(true)}
          onCancel={resetEdit}
        />}
    </DsmGrid>
  )
}

export default DairyV2FeedCompoundFeedComponent;
